// v4 ticket T4 (docs/product/spec-v4-review.md "Player + notes UI"; spec-v4-extras.md A1, A6, A9, A11; spec-v4-
// director-plus.md `safe_zones`): reviewing a finished version at /p/:id/review (?v=2&t=12.4&note=…).
// The player with what's on screen under it (scene · speaker: 'line'), the transport (play, frame steps, 0.25x /
// 0.5x, safe zones), the lanes with note pins, notes at the playhead (with marks on the paused frame), and the notes
// panel: beside the player from 1024 px, under it on a phone with each note's detail in a sheet.
// The Director's review tools (find_moment, play_moment, add_note...) are T5; they can link here with reviewPath().
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { tapTool } from "../actions";
import { api } from "../api";
import { Empty, ErrorNote, Icon, Sheet, Spinner, useLoad } from "../components/bits";
import { Shell } from "../components/Shell";
import { Lanes, type SoundPick } from "../components/review/Lanes";
import { Composer, newDraft, NoteDetail, NotesList, type Draft, type Player } from "../components/review/Notes";
import { pause, playSpan, playVersion, ReviewPlayer, seekVersion, setRate, useClock, useLoadVersion, type Media } from "../components/review/Player";
import { director } from "../director";
import {
  addLater,
  beforeAfter,
  dropLater,
  loadLater,
  pictureLine,
  saveLater,
  momentRange,
  nextSpeed,
  noteAt,
  noteTime,
  noteTypeBody,
  notesApi,
  platformParam,
  sameMedia,
  nowLine,
  openCount,
  PLATFORM_LABEL,
  platformsFor,
  reviewPath,
  setRange,
  stepFrame,
  type Note,
  type Later,
  type Platform,
  type Preview,
} from "../notes";
import { Link, navigate, useLocation } from "../router";
import { bump, set, toast, useRefresh, useStore } from "../store";
import { fmtTime } from "../timeline";
import type { Aspect, Project, Version } from "../types";

function useWide() {
  const q = "(min-width: 1024px)";
  const [wide, setWide] = useState(() => (typeof window !== "undefined" ? (window.matchMedia?.(q).matches ?? false) : false));
  useEffect(() => {
    const m = window.matchMedia?.(q);
    if (!m) return;
    const f = () => setWide(m.matches);
    m.addEventListener("change", f);
    return () => m.removeEventListener("change", f);
  }, []);
  return wide;
}

const aspectOf = (p: Project | null): Aspect | string => (p?.brief as { aspect?: string } | undefined)?.aspect ?? "16:9";

export function ReviewScreen({ id }: { id: string }) {
  const { query } = useLocation();
  const asked = Number(query.get("v")) || null;
  const tick = useRefresh("project", "notes");
  const { data: p, error: pErr } = useLoad(() => api.project(id), [id]);
  // not on every note change: a fresh signed url would reload the video and lose the playhead
  const { data: versions, error: vErr } = useLoad(() => api.versions(id), [id]);
  const version: Version | null = useMemo(() => {
    if (!versions?.length) return null;
    return (asked ? versions.find((v) => v.version === asked) : null) ?? versions.find((v) => v.isLatest) ?? [...versions].sort((a, b) => b.version - a.version)[0]!;
  }, [versions, asked]);
  const { data: vt, error: tErr } = useLoad(() => (version ? notesApi.timeline(id, version.version) : Promise.resolve(null)), [id, version?.version]);
  const { data: list, setData: setList, error: nErr } = useLoad(() => (version ? notesApi.list(id, version.version) : Promise.resolve(null)), [id, version?.version, tick]);
  const notes = list?.notes ?? [];
  const wide = useWide();

  // a play for this project shows here, not in the full-screen overlay
  useEffect(() => {
    set({ suiteProject: id });
    return () => set({ suiteProject: null });
  }, [id]);

  const tl = vt?.timeline ?? null;
  const media: Media | null = useMemo(() => (version && tl ? { url: version.url, projectId: id, version: version.version, title: `${p?.title ?? "Your video"}, v${version.version}`, durationS: tl.durationS || version.durationS } : null), [version?.version, tl, p?.title, id]); // eslint-disable-line react-hooks/exhaustive-deps
  useLoadVersion(media);

  const clock = useClock();
  // T5: the Director's play_moment plays the same file under its own signed link
  const onVersion = !!media && sameMedia(clock.src, media.url);
  const lastT = useRef(Number(query.get("t")) || 0);
  if (onVersion) lastT.current = clock.t;
  const t = lastT.current;

  // ?t= on open
  const seeded = useRef(false);
  useEffect(() => {
    if (!media || seeded.current) return;
    seeded.current = true;
    const at = Number(query.get("t"));
    if (at > 0) seekVersion(media, at);
  }, [media]); // eslint-disable-line react-hooks/exhaustive-deps
  // T5: the Director's find_moment moves ?t= while the screen is open: go there (unless something is playing)
  const qT = query.get("t");
  useEffect(() => {
    if (!media || !seeded.current || qT === null) return;
    const at = Number(qT);
    if (Number.isFinite(at) && at >= 0 && !clock.playing) seekVersion(media, at);
  }, [qT]); // eslint-disable-line react-hooks/exhaustive-deps

  const [selected, setSelected] = useState<string | null>(query.get("note"));
  const [draft, setDraftState] = useState<Draft | null>(null);
  const setDraft = useCallback((f: (d: Draft) => Draft) => setDraftState((d) => (d ? f(d) : d)), []);
  const [saving, setSaving] = useState(false);
  const [safe, setSafe] = useState<Platform | null>(() => platformParam(query.get("safe")));
  const [lanes, setLanes] = useState(false);
  const [critiquing, setCritiquing] = useState(false); // v4 (D6): Suggest notes
  // A9: listening mode (audio first, the picture said in words) and the Watch later list (this device)
  const [listen, setListen] = useState(() => query.get("listen") === "1");
  // T5: the Director's safe_zones / listen / add_note change these while the screen is open
  const qSafe = query.get("safe");
  const qListen = query.get("listen");
  const qNote = query.get("note");
  useEffect(() => {
    if (qSafe) setSafe(platformParam(qSafe));
  }, [qSafe]);
  useEffect(() => {
    if (qListen === "1") setListen(true);
  }, [qListen]);
  useEffect(() => {
    if (qNote) setSelected(qNote);
  }, [qNote]);
  const [later, setLaterState] = useState<Later[]>(() => loadLater(id));
  const setLater = (list: Later[]) => {
    setLaterState(list);
    saveLater(id, list);
  };
  const [badge, setBadge] = useState<string | null>(null);
  const chain = useRef<{ before: string; after: { url: string; start: number; end: number } } | null>(null);
  const top = useRef<HTMLDivElement>(null);
  const pb = useStore((s) => s.playback);

  const selectedNote = notes.find((n) => n.id === selected) ?? null;
  const near = clock.playing && onVersion ? (noteAt(notes, t)?.id ?? null) : null;

  // a deep-linked note: go to its moment
  const jumped = useRef<string | null>(null);
  useEffect(() => {
    if (!media || !selectedNote || jumped.current === selectedNote.id || query.get("t")) return;
    jumped.current = selectedNote.id;
    if (selectedNote.startS !== null && !clock.playing) seekVersion(media, selectedNote.startS);
  }, [media, selectedNote?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Play both: when "before" ends, play "after"
  useEffect(() => {
    const c = chain.current;
    if (!c || !media || pb.phase !== "ended" || pb.req?.url !== c.before) return;
    chain.current = null;
    setBadge("After");
    playSpan(media, c.after.url, c.after.start, c.after.end, "After the fix");
  }, [pb.phase, pb.req?.url, media]);
  // back on the version (a seek, a play): no badge
  useEffect(() => {
    if (badge === "After" && onVersion) setBadge(null);
  }, [onVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  // picking a drawing tool on a phone brings the picture into view (the tools sit under it)
  useEffect(() => {
    if (draft?.tool && !wide) document.querySelector(".rv-frame")?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [draft?.tool, wide]);

  const showPlayer = () => {
    if (!wide) top.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  };

  const player: Player = {
    moment: (n) => {
      if (!media) return;
      const r = momentRange(n, media.durationS);
      if (!r) return;
      setBadge(null);
      chain.current = null;
      if (!wide) setSelected(null);
      showPlayer();
      playSpan(media, media.url, r.start, r.end, `${media.title} at ${noteTime(n)}`);
    },
    compare: (pv: Preview, which) => {
      if (!media) return;
      const ba = beforeAfter(pv, media.url);
      if (!ba) return;
      if (!wide) setSelected(null);
      showPlayer();
      if (which === "after") {
        chain.current = null;
        setBadge("After");
        return playSpan(media, ba.after.url, ba.after.start, ba.after.end, "After the fix");
      }
      setBadge("Before");
      chain.current = which === "both" ? { before: ba.before.url, after: ba.after } : null;
      playSpan(media, ba.before.url, ba.before.start, ba.before.end, "Before the fix");
    },
  };

  const playing = clock.playing;
  const toggle = () => {
    if (!media) return;
    setBadge(null);
    chain.current = null;
    if (playing) pause();
    else playVersion(media, onVersion ? clock.t : t);
  };
  const seek = (at: number) => {
    if (!media) return;
    setBadge(null);
    chain.current = null;
    if (!onVersion && playing) pause();
    seekVersion(media, at);
    if (!onVersion) lastT.current = at;
  };
  const step = (dir: 1 | -1, big = false) => {
    if (!media) return;
    if (playing) pause();
    seek(big ? Math.max(0, Math.min(media.durationS, t + dir)) : stepFrame(t, dir, media.durationS));
  };

  const addNote = (o: Partial<Draft> = {}) => {
    if (!media) return;
    if (playing) pause();
    setSelected(null);
    setDraftState(newDraft(t, o));
  };

  const save = async () => {
    if (!draft || !version || !media) return;
    setSaving(true);
    const kb = noteTypeBody(draft.type);
    try {
      const n = await notesApi.create(id, version.version, {
        body: draft.body.trim(),
        kind: kb.kind,
        ...(kb.general ? { general: true } : { start: draft.start, ...(draft.end !== null ? { end: draft.end } : {}) }),
        ...(draft.marks.length && !kb.general ? { marks: draft.marks } : {}),
        ...(draft.refs.length ? { ref_asset_ids: draft.refs.map((a) => a.id) } : {}),
        ...(draft.parts?.length ? { parts: draft.parts } : {}),
      });
      setList((l) => (l ? { ...l, notes: [...l.notes.filter((x) => x.id !== n.id), n] } : l));
      setDraftState(null);
      setSelected(n.id);
      director.context(`The user added a ${draft.type === "general" ? "note on the whole video" : `${draft.type} note at ${noteTime(n)}`} of version ${version.version} by tapping: "${n.body}".`);
      // a change or a question gets its plan / answer straight away (planning is free)
      if (n.kind !== "like")
        notesApi
          .plan(n.id)
          .then((r) => upsert(r.note))
          .catch(() => undefined);
    } catch (e) {
      toast(`Couldn't add the note. ${(e as Error).message}`, "bad");
    } finally {
      setSaving(false);
    }
  };

  const upsert = (n: Note) => setList((l) => (l ? { ...l, notes: l.notes.map((x) => (x.id === n.id ? n : x)) } : l));
  const drop = (nid: string) => {
    setList((l) => (l ? { ...l, notes: l.notes.filter((x) => x.id !== nid) } : l));
    setSelected(null);
  };

  const sound = (s: SoundPick) => {
    if (s.kind === "line") {
      seek(s.line.startS);
      addNote({ start: s.line.startS, end: s.line.endS, parts: ["voice"], on: `${s.line.speaker}'s line "${s.line.text.length > 40 ? `${s.line.text.slice(0, 39)}…` : s.line.text}"` });
    } else if (s.kind === "music") {
      addNote({ start: t, end: null, parts: ["music"], on: `the music (${s.music.label})` });
    } else {
      seek(s.sfx.atS);
      addNote({ start: s.sfx.atS, end: s.sfx.endS ?? s.sfx.atS + 0.6, parts: ["sfx"], on: `the ${s.sfx.label} sound` });
    }
  };

  // keys (desktop): Space, ←/→ (Shift: a second), , and . (a frame), I / O, N, Esc
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.closest("input, textarea, select, [contenteditable=true]") || el.closest(".sheet"))) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key;
      if (k === " ") {
        if (el?.closest("button")) return;
        e.preventDefault();
        toggle();
      } else if (k === "ArrowLeft" || k === ",") {
        e.preventDefault();
        step(-1, e.shiftKey);
      } else if (k === "ArrowRight" || k === ".") {
        e.preventDefault();
        step(1, e.shiftKey);
      } else if (k === "i" || k === "I") {
        if (!draft) addNote();
        else setDraft((d) => ({ ...d, ...setRange(d, "in", t, media?.durationS ?? 0) }));
      } else if (k === "o" || k === "O") {
        if (!draft) addNote({ start: Math.max(0, t - 2), end: t });
        else setDraft((d) => ({ ...d, ...setRange(d, "out", t, media?.durationS ?? 0) }));
      } else if (k === "n" || k === "N") {
        e.preventDefault();
        if (!draft) addNote();
      } else if (k === "Escape") {
        if (draft) setDraftState(null);
        else setSelected(null);
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });

  // ------------------------------------------------------------------ states

  const err = pErr ?? vErr ?? tErr ?? nErr;
  if (err && !(p && versions && vt))
    return (
      <Shell>
        <div className="page">
          <Link to={`/p/${encodeURIComponent(id)}`} className="back">
            <Icon name="back" size={18} /> Back to the video
          </Link>
          <ErrorNote text={`Couldn't open the review. ${err}`} />
        </div>
      </Shell>
    );
  if (versions && !versions.length)
    return (
      <Shell>
        <div className="page">
          <Link to={`/p/${encodeURIComponent(id)}`} className="back">
            <Icon name="back" size={18} /> Back to the video
          </Link>
          <Empty title="Nothing to review yet" action={<Link to={`/p/${encodeURIComponent(id)}`} className="btn btn-primary">Back to the video</Link>}>
            Render the video first. Each render becomes a version you can review here, moment by moment.
          </Empty>
        </div>
      </Shell>
    );
  if (!p || !version || !tl || !media)
    return (
      <Shell>
        <div className="page">
          <Spinner label="Opening the review" />
        </div>
      </Shell>
    );

  const aspect = aspectOf(p);
  const platforms = platformsFor(aspect);
  const drawing = draft?.tool && draft.type !== "general" ? { tool: draft.tool, atS: t, marks: draft.marks, onAdd: (m: typeof draft.marks[number]) => setDraft((d) => ({ ...d, marks: [...d.marks, m] })) } : null;
  const shownMarks = draft ? draft.marks : selectedNote && selectedNote.marks.length && !playing && Math.abs(t - (selectedNote.marks[0]?.atS ?? selectedNote.startS ?? -9)) < 0.5 ? selectedNote.marks : [];
  const open = openCount(notes);
  const now = onVersion || !badge ? nowLine(tl, t) : badge === "After" ? "After the fix (preview)" : "Before the fix";
  const detail = (n: Note) => <NoteDetail n={n} onChange={upsert} onDelete={drop} player={player} />;
  const sorted = [...(versions ?? [])].sort((a, b) => b.version - a.version);

  return (
    <Shell>
      <div className={`page rv-page${draft ? " is-composing" : ""}`} ref={top}>
        <header className="rv-top">
          <Link to={`/p/${encodeURIComponent(id)}`} className="back">
            <Icon name="back" size={18} /> {p.title}
          </Link>
          <div className="rv-title">
            <h1>Review</h1>
            {sorted.length > 1 ? (
              <label className="rv-ver">
                <span className="sr-only">Version</span>
                <select value={version.version} onChange={(e) => navigate(reviewPath(id, { v: Number(e.target.value) }), { replace: true })}>
                  {sorted.map((v) => (
                    <option key={v.version} value={v.version}>
                      v{v.version}
                      {v.isLatest ? " (in use)" : ""}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <span className="rv-ver-tag">v{version.version}</span>
            )}
            <span className="rv-open">{open ? `${open} open` : notes.length ? "All done" : ""}</span>
          </div>
        </header>

        <div className="rv-grid">
          <div className="rv-main">
            <ReviewPlayer aspect={aspect} poster={version.thumbUrl ?? p.thumbUrl ?? undefined} marks={shownMarks} draw={drawing} safe={safe} badge={badge} listen={listen} onTapPicture={toggle}>
              {listen && (
                <div className="rv-listen" aria-live="polite">
                  <p className="rv-listen-kicker">Listening</p>
                  <p className="rv-listen-pic">{pictureLine(tl, t) || "The picture is between scenes."}</p>
                </div>
              )}
              <p className="rv-now" aria-live="polite">
                {now || " "}
              </p>
              <div className="rv-transport">
                <button data-action="review.play" type="button" className="rv-play" onClick={toggle} aria-label={playing ? "Pause" : "Play"}>
                  <Icon name={playing ? "pause" : "play"} size={20} />
                </button>
                <span className="rv-clock">
                  {fmtTime(t)} <span>/ {fmtTime(media.durationS)}</span>
                </span>
                <button type="button" className="icon-btn" onClick={() => step(-1)} aria-label="Back one frame" title="Back one frame">
                  <FrameIcon dir={-1} />
                </button>
                <button type="button" className="icon-btn" onClick={() => step(1)} aria-label="Forward one frame" title="Forward one frame">
                  <FrameIcon dir={1} />
                </button>
                <button type="button" className="rv-speed" onClick={() => setRate(nextSpeed(clock.rate))} aria-label={`Speed ${clock.rate}x. Tap to change`}>
                  {clock.rate}x
                </button>
                <label className="rv-safe-pick">
                  <span className="sr-only">Safe zones</span>
                  <select data-action="review.safe" value={safe ?? ""} onChange={(e) => setSafe((e.target.value || null) as Platform | null)} aria-label="Safe zones">
                    <option value="">Safe zones</option>
                    {platforms.map((x) => (
                      <option key={x} value={x}>
                        {PLATFORM_LABEL[x]}
                      </option>
                    ))}
                  </select>
                </label>
                <button data-action="review.listen" type="button" className={`rv-lanes-toggle${listen ? " is-on" : ""}`} aria-pressed={listen} onClick={() => setListen((v) => !v)} title="Audio first: the picture is said in words">
                  Listen
                </button>
                <button
                  data-action="review.later"
                  type="button"
                  className="rv-lanes-toggle"
                  aria-label={`Watch ${fmtTime(t)} later`}
                  title="Save this moment to watch later"
                  onClick={() => {
                    setLater(addLater(later, { t: Math.round(t * 10) / 10, version: version.version, label: nowLine(tl, t) || `At ${fmtTime(t)}`, at: new Date().toISOString() }));
                    toast(`Saved ${fmtTime(t)} for later.`, "good");
                  }}
                >
                  Later
                </button>
                {!wide && (
                  <button type="button" className={`rv-lanes-toggle${lanes ? " is-on" : ""}`} aria-pressed={lanes} onClick={() => setLanes((v) => !v)}>
                    Lanes
                  </button>
                )}
                {!draft && (
                  <button data-action="review.add-note" type="button" className="btn btn-primary rv-add" onClick={() => addNote()} aria-label={`Add a note at ${fmtTime(t)}`}>
                    <Icon name="plus" size={16} /> {wide ? "Add a note" : `Add a note at ${fmtTime(t)}`}
                  </button>
                )}
              </div>
            </ReviewPlayer>
            <Lanes
              tl={tl}
              notes={notes}
              t={t}
              range={draft && draft.type !== "general" ? { start: draft.start, end: draft.end } : null}
              selected={selected}
              full={wide || lanes}
              onSeek={seek}
              onRange={(start, end) => {
                if (draft) setDraft((d) => ({ ...d, start, end }));
                else addNote({ start, end });
                seek(start);
              }}
              onSound={sound}
              onPin={(n) => {
                setDraftState(null);
                setSelected(n.id);
                if (n.startS !== null) seek(n.startS);
              }}
            />
          </div>

          <aside className="rv-side" aria-label="Notes">
            {draft && <Composer draft={draft} setDraft={setDraft} t={t} projectId={id} saving={saving} onSave={save} onCancel={() => setDraftState(null)} />}
            {later.length > 0 && (
              <section className="rv-later" aria-label="Watch later">
                <h3 className="rv-sub">Watch later</h3>
                <ol>
                  {later.map((l) => (
                    <li key={`${l.version}:${l.t}`}>
                      <button
                        type="button"
                        className="rv-later-go"
                        onClick={() => (l.version === version.version ? seek(l.t) : navigate(reviewPath(id, { v: l.version, t: l.t }), { replace: true }))}
                      >
                        <span className="rv-note-time">
                          {fmtTime(l.t)}
                          {l.version !== version.version ? ` · v${l.version}` : ""}
                        </span>
                        <span>{l.label}</span>
                      </button>
                      <button type="button" className="icon-btn" aria-label={`Remove ${fmtTime(l.t)} from Watch later`} onClick={() => setLater(dropLater(later, l))}>
                        <Icon name="close" size={14} />
                      </button>
                    </li>
                  ))}
                </ol>
              </section>
            )}
            <header className="rv-side-head">
              <h2>Notes</h2>
              <span className="muted">{notes.length ? `${notes.length} on v${version.version}` : ""}</span>
              {/* v4 (D6 parity with critique): the Director watches this version and adds 3-5 open notes. Free. */}
              <button
                type="button"
                className="btn btn-quiet btn-small rv-critique"
                data-action="review.critique"
                disabled={critiquing}
                onClick={async () => {
                  setCritiquing(true);
                  try {
                    const r = await tapTool("critique", { project_id: id, version: version.version }, `asked for the Director's notes on v${version.version}`);
                    if (r) bump("notes");
                  } finally {
                    setCritiquing(false);
                  }
                }}
              >
                <Icon name="sparkle" size={14} /> {critiquing ? "Watching…" : "Suggest notes"}
              </button>
            </header>
            <NotesList
              notes={notes}
              selected={selected}
              near={near}
              onSelect={(n) => {
                setDraftState(null);
                const same = selected === n.id;
                setSelected(same && wide ? null : n.id);
                if (!same && n.startS !== null && !playing) seek(n.startS);
              }}
              detail={wide ? detail : undefined}
            />
          </aside>
        </div>
        {!wide && selectedNote && (
          <Sheet title={`${selectedNote.kind === "like" ? "Like" : selectedNote.kind === "question" ? "Question" : "Note"} ${selectedNote.general ? "on the whole video" : `at ${noteTime(selectedNote)}`}`} onClose={() => setSelected(null)} className="rv-sheet">
            <div className="sheet-body">
              {selectedNote.body && <p className="rv-sheet-body">{selectedNote.body}</p>}
              {detail(selectedNote)}
            </div>
          </Sheet>
        )}
      </div>
    </Shell>
  );
}

function FrameIcon({ dir }: { dir: 1 | -1 }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden fill="currentColor">
      {dir === 1 ? (
        <>
          <path d="M4 4.2v9.6a.6.6 0 0 0 .9.5l7.2-4.8a.6.6 0 0 0 0-1L4.9 3.7a.6.6 0 0 0-.9.5Z" />
          <rect x="13" y="4" width="1.8" height="10" rx=".6" />
        </>
      ) : (
        <>
          <path d="M14 4.2v9.6a.6.6 0 0 1-.9.5L5.9 9.5a.6.6 0 0 1 0-1l7.2-4.8a.6.6 0 0 1 .9.5Z" />
          <rect x="3.2" y="4" width="1.8" height="10" rx=".6" />
        </>
      )}
    </svg>
  );
}
